import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ClinicWhatsAppTemplateRow = {
  clinic_id: string;
  vaccines_pending_template: string;
  updated_at: string | null;
};

export function useClinicWhatsAppTemplate(clinicId: string | null) {
  return useQuery({
    queryKey: ["clinic-whatsapp-template", clinicId],
    queryFn: async (): Promise<ClinicWhatsAppTemplateRow | null> => {
      if (!clinicId) return null;

      const { data, error } = await supabase
        .from("clinic_whatsapp_templates")
        .select("clinic_id, vaccines_pending_template, updated_at")
        .eq("clinic_id", clinicId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      return data as unknown as ClinicWhatsAppTemplateRow;
    },
    enabled: !!clinicId,
    staleTime: 1000 * 30,
  });
}

export function useUpsertClinicWhatsAppTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      clinicId: string;
      vaccinesPendingTemplate: string;
    }) => {
      const { clinicId, vaccinesPendingTemplate } = input;

      const { error } = await supabase.from("clinic_whatsapp_templates").upsert(
        {
          clinic_id: clinicId,
          vaccines_pending_template: vaccinesPendingTemplate,
        },
        { onConflict: "clinic_id" }
      );

      if (error) throw error;
      return true;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["clinic-whatsapp-template", variables.clinicId],
      });
    },
  });
}
